import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { DebouncedInputSearch } from '@components/debounced-input-search';
import { Divider } from '@tremor/react';
import classNames from 'classnames';

import { CheckboxItem } from '../components/checkbox-item';
import { ClearButton } from '../components/clear-button';
import LazyNestedCheckboxOptionItem from '../components/lazy-nested-checkbox-option-item';
import { filterByName, getItem, isPartiallySelected, toggleSelection, updateParentStates } from '../utils';

import { NestedCheckboxItem, NestedCheckboxListProps } from './nested-checkbox-list.type';

export const NestedCheckboxList = forwardRef<HTMLDivElement, NestedCheckboxListProps>(
  (
    {
      data,
      onChange,
      selectedIds,
      clearText,
      clearable,
      selectAllId,
      canSelectAll,
      selectAllText,
      checkboxOptionClassName,
      customStyles,
      canSearch,
      searchText,
    },
    ref
  ) => {
    const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(new Set(selectedIds ?? []));

    useEffect(() => {
      const newIds = selectedIds ?? [];
      setLocalSelectedIds((prev) => {
        if (prev.size === newIds.length && newIds.every((id) => prev.has(id))) {
          return prev;
        }
        return new Set(newIds);
      });
    }, [selectedIds]);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const filteredData = useMemo(() => filterByName(searchTerm, data), [searchTerm, data]);

    const visibleIds = useMemo(() => {
      const ids: string[] = [];
      const extractIds = (items: NestedCheckboxItem[]) => {
        items.forEach((item) => {
          ids.push(item.id);
          if (item.children) extractIds(item.children);
        });
      };
      extractIds(filteredData);
      return ids;
    }, [filteredData]);

    const allSelected = useMemo(() => {
      if (visibleIds.length === 0) return false;
      return visibleIds.every((id) => localSelectedIds.has(id));
    }, [visibleIds, localSelectedIds]);

    const isIndeterminate = useMemo(() => {
      if (visibleIds.length === 0) return false;
      const someSelected = visibleIds.some((id) => localSelectedIds.has(id));
      return someSelected && !allSelected;
    }, [visibleIds, localSelectedIds, allSelected]);

    const handleSelection = useCallback(
      (id: string, selectChildren = false) => {
        const node = getItem(id, data);
        if (!node) {
          return;
        }
        const shouldBeSelected = !localSelectedIds.has(node.id);
        toggleSelection(node, shouldBeSelected, localSelectedIds);
        if (selectChildren) {
          node.children?.forEach((child) => {
            toggleSelection(child, shouldBeSelected, localSelectedIds);
          });
        }
        updateParentStates(node, data, localSelectedIds);
        const nextSelected = new Set(localSelectedIds);
        setLocalSelectedIds(nextSelected);
        onChange(Array.from(nextSelected));
      },
      [data, localSelectedIds, onChange]
    );

    const clearSelection = () => {
      setLocalSelectedIds(new Set());
      onChange([]);
      setSearchTerm('');
    };

    const toggleAll = () => {
      const shouldBeSelected = !allSelected;
      filteredData.forEach((item: NestedCheckboxItem) => {
        toggleSelection(item, shouldBeSelected, localSelectedIds);
        // After toggling a branch in filteredData, we should update parents in the global tree
        // to handle the cases where parents might be outside the filtered scope but affected.
        updateParentStates(item, data, localSelectedIds);
      });
      const nextSelected = new Set(localSelectedIds);
      setLocalSelectedIds(nextSelected);
      onChange(Array.from(nextSelected));
    };

    const handleSearch = (value: string) => {
      setSearchTerm(value.toLowerCase());
    };

    const isSelected = useCallback((id: string) => localSelectedIds.has(id), [localSelectedIds]);

    const showClearButton = clearable && clearText;
    const showSelectAllCheckbox = canSelectAll && selectAllText && selectAllId;
    const mergedCustomStyles = useMemo(() => ({ paddingLeft: '16px', ...customStyles }), [customStyles]);

    return (
      <div ref={ref}>
        {(canSearch || showClearButton) && (
          <header
            className={classNames('sticky xs:relative sm:sticky top-0 z-10 pt-4', checkboxOptionClassName, {
              'bg-primary': !checkboxOptionClassName,
            })}>
            <div className='mb-5 flex gap-4'>
              {canSearch && (
                <DebouncedInputSearch
                  onChange={handleSearch}
                  placeholder={searchText}
                  defaultValue={searchTerm}
                  variant={'dark'}
                  paddingClasses='shrink-0'
                  canDeleteSearch
                  className='w-full'
                />
              )}
              {showClearButton && (
                <ClearButton
                  itemsCount={localSelectedIds.size}
                  onClick={clearSelection}
                  label={clearText}
                  isFullSize={!canSearch}
                />
              )}
            </div>
            <Divider className='my-4' />
          </header>
        )}
        {showSelectAllCheckbox && (
          <CheckboxItem
            id={selectAllId}
            customStyles={mergedCustomStyles}
            isSelected={allSelected}
            onChange={toggleAll}
            label={selectAllText}
            isIndeterminated={isIndeterminate}
          />
        )}
        {filteredData.map((item: NestedCheckboxItem) => (
          <LazyNestedCheckboxOptionItem
            key={item.id}
            item={item}
            onSelect={handleSelection}
            isSelected={isSelected}
            isExpanded={searchTerm !== '' || isPartiallySelected(item, isSelected)}
            checkboxOptionClassName={checkboxOptionClassName}
            customStyles={customStyles}
          />
        ))}
      </div>
    );
  }
);

export default NestedCheckboxList;
